import { useMemo, useCallback } from 'react';
import type { FilterGroup, DisplayContent } from './types';

export function useMultiSelect(
  groups: FilterGroup[],
  selectedItems: string[],
  onItemsChange: (items: string[]) => void,
  maxDisplayItems: number = 3
) {
  const allItems = useMemo(() => {
    return groups.flatMap((group) => group.items);
  }, [groups]);

  const selectedCount = selectedItems.length;
  const totalCount = allItems.length;
  const isAllSelected = selectedCount === totalCount && totalCount > 0;

  const handleSelectAll = useCallback(() => {
    onItemsChange(allItems.map((item) => item.id));
  }, [allItems, onItemsChange]);

  const handleClearAll = useCallback(() => {
    onItemsChange([]);
  }, [onItemsChange]);

  const handleToggleItem = useCallback(
    (itemId: string) => {
      if (selectedItems.includes(itemId)) {
        onItemsChange(selectedItems.filter((id) => id !== itemId));
      } else {
        onItemsChange([...selectedItems, itemId]);
      }
    },
    [selectedItems, onItemsChange]
  );

  const isItemSelected = useCallback(
    (itemId: string) => {
      return selectedItems.includes(itemId);
    },
    [selectedItems]
  );

  const getDisplayContent = useCallback((): DisplayContent => {
    if (selectedCount === 0) {
      return { type: 'placeholder' };
    }

    if (isAllSelected) {
      return { type: 'all' };
    }

    const selectedItemObjects = allItems.filter((item) => selectedItems.includes(item.id));
    const displayItems = selectedItemObjects.slice(0, maxDisplayItems);
    const remainingCount = selectedCount - displayItems.length;

    return {
      type: 'selected',
      displayItems: displayItems.map((item) => item.name),
      remainingCount
    };
  }, [selectedCount, isAllSelected, allItems, selectedItems, maxDisplayItems]);

  return {
    // Data
    allItems,
    selectedCount,
    totalCount,
    isAllSelected,

    // Actions
    handleSelectAll,
    handleClearAll,
    handleToggleItem,
    isItemSelected,

    displayContent: getDisplayContent()
  };
}
